/**
 * 生成任务的浏览器补偿索引。后端数据库是唯一真相源，localStorage 只用于刷新/断网恢复 batchId。
 */
import { authState } from '@/services/auth'

const STORAGE_KEY_PREFIX = 'yuntu.pending-generation-records.v2'
const MAX_AGE = 24 * 60 * 60 * 1000
const ORPHAN_GRACE_AGE = 5 * 60 * 1000

function storageAvailable() {
  return typeof window !== 'undefined' && window.localStorage
}

function storageKey() {
  const userId = authState.user && authState.user.id
  return `${STORAGE_KEY_PREFIX}.${userId || 'anonymous'}`
}

function readAll() {
  if (!storageAvailable()) return []
  try {
    const value = JSON.parse(window.localStorage.getItem(storageKey()) || '[]')
    if (!Array.isArray(value)) return []
    const now = Date.now()
    return value.filter(item => item && item.id && (!item.createdAt || now - new Date(item.createdAt).getTime() < MAX_AGE))
  } catch (error) {
    return []
  }
}

function writeAll(records) {
  if (!storageAvailable()) return
  try {
    window.localStorage.setItem(storageKey(), JSON.stringify(records))
  } catch (error) {
    // The backend remains the source of truth when browser storage is unavailable.
  }
}

export function pendingGenerationRecords(stage) {
  const records = readAll()
  writeAll(records)
  return stage ? records.filter(item => item.stage === stage) : records
}

export function savePendingGeneration(record) {
  if (!record || !record.id) return
  const records = readAll().filter(item => item.id !== record.id)
  records.unshift({ ...record, createdAt: record.createdAt || new Date().toISOString() })
  writeAll(records.slice(0, 30))
}

export function removePendingGeneration(id) {
  if (!id) return
  writeAll(readAll().filter(item => item.id !== id))
}

function hasInputContextImages(inputContext) {
  if (!inputContext || typeof inputContext !== 'object') return false
  const floorPlanImages = Array.isArray(inputContext.floorPlanImages) ? inputContext.floorPlanImages : []
  const referenceImages = Array.isArray(inputContext.referenceImages) ? inputContext.referenceImages : []
  return floorPlanImages.some(item => item && item.url) ||
    referenceImages.some(item => item && item.url) ||
    Boolean(inputContext.annotation)
}

export function mergePendingGenerations(serverRecords, stage) {
  // 服务器记录优先；本地独有记录只在宽限期内保留，防止旧任务长期占据界面。
  const records = Array.isArray(serverRecords) ? serverRecords : []
  const serverIds = new Set(records.map(item => item && item.id).filter(Boolean))
  const now = Date.now()
  const pending = pendingGenerationRecords(stage).filter(item => {
    if (serverIds.has(item.id)) return true
    const createdAt = new Date(item.createdAt || 0).getTime()
    const isOrphaned = Number.isFinite(createdAt) && now - createdAt > ORPHAN_GRACE_AGE
    if (isOrphaned) removePendingGeneration(item.id)
    return !isOrphaned
  })
  const pendingById = new Map(pending.map(item => [item.id, item]))
  const merged = records.map(record => {
    const local = pendingById.get(record.id)
    if (!local) return record
    pendingById.delete(record.id)
    const taskCount = Array.isArray(record.tasks) ? record.tasks.length : 0
    if (taskCount >= Number(local.expectedCount || 1) || record.status === 'failed') {
      removePendingGeneration(record.id)
    }
    return {
      ...local,
      ...record,
      expectedCount: Math.max(Number(local.expectedCount || 1), taskCount),
      // The API always returns an inputContext shell. Do not let empty arrays erase
      // the uploaded source image retained by the optimistic browser record.
      inputContext: hasInputContextImages(record.inputContext) ? record.inputContext : local.inputContext
    }
  })
  const localOnly = [...pendingById.values()].map(record => ({ ...record, localOnly: true }))
  return [...localOnly, ...merged]
}
